import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { isAdminPath } from '../constants/adminRoutes'

const LanguageContext = createContext(null)

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const inAdmin = isAdminPath(location.pathname)
  const [language, setLanguageState] = useState(
    () => localStorage.getItem('mayar_lang') || 'en',
  )

  const effectiveLanguage = inAdmin ? 'en' : language

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang)
    localStorage.setItem('mayar_lang', lang)
  }, [])

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'en' ? 'ar' : 'en')
  }, [language, setLanguage])

  useEffect(() => {
    i18n.changeLanguage(effectiveLanguage)
    const dir = effectiveLanguage === 'ar' ? 'rtl' : 'ltr'
    document.documentElement.lang = effectiveLanguage
    document.documentElement.dir = dir
  }, [effectiveLanguage, i18n])

  const isRTL = effectiveLanguage === 'ar'

  const localized = useCallback(
    (item, field = 'title') => {
      if (!item) return ''
      const enKey = `${field}_en`
      const arKey = `${field}_ar`
      if (effectiveLanguage === 'ar') {
        return item[arKey] || item[arKey.replace('_ar', '')] || item[field] || item[enKey] || ''
      }
      return item[enKey] || item[field] || item[arKey] || ''
    },
    [effectiveLanguage],
  )

  const value = useMemo(
    () => ({
      language: effectiveLanguage,
      siteLanguage: language,
      setLanguage,
      toggleLanguage,
      isRTL,
      localized,
      inAdmin,
    }),
    [effectiveLanguage, language, setLanguage, toggleLanguage, isRTL, localized, inAdmin],
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}
